import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';

// Nodes spread across full 1200×680 canvas
const HOME_NODES = [
  { id: 'patient',   x: 600, y: 200, label: 'Patient ID',  r: 36, color: '#38bdf8' },
  { id: 'fhir',      x: 900, y: 160, label: 'FHIR R4',     r: 34, color: '#38bdf8' },
  { id: 'icd',       x: 760, y: 90,  label: 'ICD-10',      r: 28, color: '#34d399' },
  { id: 'schema',    x: 180, y: 120, label: 'Schema',       r: 26, color: '#94a3b8' },
  { id: 'dob',       x: 100, y: 280, label: 'DOB',          r: 24, color: '#818cf8' },
  { id: 'diagnosis', x: 350, y: 380, label: 'Diagnosis',    r: 28, color: '#f472b6' },
  { id: 'admit',     x: 750, y: 390, label: 'Admission',    r: 26, color: '#fb923c' },
  { id: 'lab',       x: 1050,y: 280, label: 'Lab Result',   r: 24, color: '#34d399' },
  { id: 'med',       x: 980, y: 430, label: 'Medication',   r: 26, color: '#a78bfa' },
  { id: 'vitals',    x: 200, y: 480, label: 'Vitals',       r: 22, color: '#fb923c' },
  { id: 'encounter', x: 480, y: 520, label: 'Encounter',    r: 24, color: '#818cf8' },
  { id: 'provider',  x: 1100,y: 120, label: 'Provider',     r: 22, color: '#94a3b8' },
  { id: 'insurer',   x: 80,  y: 530, label: 'Insurer',      r: 22, color: '#f472b6' },
  { id: 'procedure', x: 860, y: 560, label: 'Procedure',    r: 24, color: '#34d399' },
];

const EDGES = [
  ['patient', 'fhir'],
  ['patient', 'icd'],
  ['patient', 'dob'],
  ['patient', 'diagnosis'],
  ['patient', 'admit'],
  ['fhir',    'icd'],
  ['fhir',    'lab'],
  ['fhir',    'med'],
  ['fhir',    'provider'],
  ['icd',     'diagnosis'],
  ['schema',  'patient'],
  ['schema',  'dob'],
  ['diagnosis','admit'],
  ['admit',   'med'],
  ['admit',   'encounter'],
  ['vitals',  'encounter'],
  ['vitals',  'dob'],
  ['insurer', 'encounter'],
  ['insurer', 'vitals'],
  ['procedure','admit'],
  ['procedure','med'],
  ['lab',     'provider'],
];

const REPEL_RADIUS = 110;
const MAX_PUSH = 55;
const LERP = 0.08;

// Per-node ambient drift parameters (deterministic random-ish values)
const DRIFT = HOME_NODES.map((_, i) => ({
  ampX:   8  + (i * 3.7) % 7,
  ampY:   6  + (i * 2.9) % 8,
  speedX: 0.0004 + (i * 0.000037) % 0.0003,
  speedY: 0.0003 + (i * 0.000041) % 0.0003,
  phaseX: (i * 1.618) % (Math.PI * 2),
  phaseY: (i * 2.399) % (Math.PI * 2),
}));

export default function Hero() {
  const svgRef = React.useRef(null);
  // Refs for each node's <g> element so we can mutate directly
  const nodeGroupRefs = React.useRef({});
  // Refs for each edge <line> element
  const edgeLineRefs = React.useRef([]);
  // Current lerped positions per node
  const posRef = React.useRef(HOME_NODES.map((n) => ({ cx: n.x, cy: n.y })));
  // Mouse position in SVG viewBox coords (null when outside)
  const mouseSVGRef = React.useRef(null);
  const rafRef = React.useRef(null);

  // Convert client coords to SVG viewBox coords
  const toSVGCoords = React.useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    return {
      x: (clientX - rect.left) * (1200 / rect.width),
      y: (clientY - rect.top)  * (680  / rect.height),
    };
  }, []);

  const handleMouseMove = React.useCallback((e) => {
    mouseSVGRef.current = toSVGCoords(e.clientX, e.clientY);
  }, [toSVGCoords]);

  const handleMouseLeave = React.useCallback(() => {
    mouseSVGRef.current = null;
  }, []);

  // Persistent rAF loop — starts on mount, stops on unmount
  React.useEffect(() => {
    const nodeIndexById = {};
    HOME_NODES.forEach((n, i) => { nodeIndexById[n.id] = i; });

    function frame(t) {
      rafRef.current = requestAnimationFrame(frame);
      const pos = posRef.current;
      const mouse = mouseSVGRef.current;

      HOME_NODES.forEach((node, i) => {
        const d = DRIFT[i];
        // Ambient target: home position + sinusoidal drift
        let tx = node.x + Math.sin(t * d.speedX + d.phaseX) * d.ampX;
        let ty = node.y + Math.sin(t * d.speedY + d.phaseY) * d.ampY;

        // Mouse repulsion on top of ambient target
        if (mouse) {
          const dx = tx - mouse.x;
          const dy = ty - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < REPEL_RADIUS && dist > 0.1) {
            const push = ((REPEL_RADIUS - dist) / REPEL_RADIUS) * MAX_PUSH;
            tx += (dx / dist) * push;
            ty += (dy / dist) * push;
          }
        }

        // Lerp current position toward target
        pos[i].cx += (tx - pos[i].cx) * LERP;
        pos[i].cy += (ty - pos[i].cy) * LERP;

        // Directly update SVG DOM
        const g = nodeGroupRefs.current[node.id];
        if (g) {
          const circles = g.querySelectorAll('circle');
          const text = g.querySelector('text');
          const cx = pos[i].cx;
          const cy = pos[i].cy;
          if (circles[0]) { circles[0].setAttribute('cx', cx); circles[0].setAttribute('cy', cy); }
          if (circles[1]) { circles[1].setAttribute('cx', cx); circles[1].setAttribute('cy', cy); }
          if (text)        { text.setAttribute('x', cx);        text.setAttribute('y', cy + 1); }
        }
      });

      // Update edges
      EDGES.forEach(([a, b], i) => {
        const ia = nodeIndexById[a];
        const ib = nodeIndexById[b];
        const line = edgeLineRefs.current[i];
        if (line && ia != null && ib != null) {
          line.setAttribute('x1', pos[ia].cx);
          line.setAttribute('y1', pos[ia].cy);
          line.setAttribute('x2', pos[ib].cx);
          line.setAttribute('y2', pos[ib].cy);
        }
      });
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <Box
      id="hero"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        width: '100%',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #060e1e 0%, #0a1628 50%, #07111f 100%)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
      }}
    >
      {/* Full-screen graph background */}
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <svg
          ref={svgRef}
          viewBox="0 0 1200 680"
          preserveAspectRatio="xMidYMid slice"
          style={{ width: '100%', height: '100%' }}
        >
          <defs>
            {HOME_NODES.map((n) => (
              <radialGradient key={`g-${n.id}`} id={`g-${n.id}`} cx="35%" cy="35%" r="65%">
                <stop offset="0%"   stopColor={n.color} stopOpacity="0.4" />
                <stop offset="100%" stopColor={n.color} stopOpacity="0.07" />
              </radialGradient>
            ))}
            <filter id="glow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Edges — initial positions; rAF loop updates x1/y1/x2/y2 directly */}
          {EDGES.map(([a, b], i) => {
            const na = HOME_NODES.find((n) => n.id === a);
            const nb = HOME_NODES.find((n) => n.id === b);
            if (!na || !nb) return null;
            return (
              <line
                key={i}
                ref={(el) => { edgeLineRefs.current[i] = el; }}
                x1={na.x} y1={na.y}
                x2={nb.x} y2={nb.y}
                stroke="#7dd3fc"
                strokeOpacity="0.15"
                strokeWidth="1.5"
              />
            );
          })}

          {/* Nodes — initial positions; rAF loop moves circles/text directly */}
          {HOME_NODES.map((node) => (
            <g
              key={node.id}
              ref={(el) => { nodeGroupRefs.current[node.id] = el; }}
              filter="url(#glow)"
            >
              {/* Outer pulse ring */}
              <circle
                cx={node.x} cy={node.y} r={node.r + 8}
                fill="none"
                stroke={node.color}
                strokeOpacity="0.12"
                strokeWidth="1"
              />
              {/* Main node */}
              <circle
                cx={node.x} cy={node.y} r={node.r}
                fill={`url(#g-${node.id})`}
                stroke={node.color}
                strokeOpacity="0.8"
                strokeWidth="1.8"
              />
              {/* Label */}
              <text
                x={node.x} y={node.y + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={node.color}
                fontSize={node.r > 30 ? 10 : 9}
                fontFamily="monospace"
                fontWeight="700"
                style={{ userSelect: 'none' }}
              >
                {node.label}
              </text>
            </g>
          ))}
        </svg>
      </Box>

      {/* Subtle radial vignette so center text stays readable */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 60% 55% at 50% 50%, rgba(6,14,30,0.55) 0%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Text content */}
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: { xs: 14, sm: 10 }, textAlign: 'center' }}>
        <Stack spacing={3} alignItems="center">
          <Typography
            variant="h1"
            sx={{
              fontSize: 'clamp(2.4rem, 5vw, 3.5rem)',
              fontWeight: 800,
              color: '#f0f9ff',
              lineHeight: 1.15,
              textShadow: '0 2px 24px rgba(6,14,30,0.8)',
            }}
          >
            Ship compliant AI without touching real patient data
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#bae6fd',
              fontSize: '1.1rem',
              lineHeight: 1.7,
              maxWidth: 600,
              textShadow: '0 1px 12px rgba(6,14,30,0.9)',
            }}
          >
            Cynthia generates HIPAA-safe synthetic datasets that mirror your production schemas — so your team can build, test, and train ML models without waiting for de-identified data.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
            <Button
              component={Link}
              to="/signup"
              variant="contained"
              size="large"
              sx={{ fontWeight: 700, px: 4 }}
            >
              Start Free — No Credit Card
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() =>
                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
              }
              sx={{
                borderColor: '#1e3a5f',
                color: '#7dd3fc',
                '&:hover': { borderColor: '#38bdf8', color: '#38bdf8', bgcolor: 'rgba(56,189,248,0.06)' },
              }}
            >
              See How It Works
            </Button>
          </Stack>
          <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1} sx={{ pt: 0.5 }}>
            {[
              '10,000+ datasets generated',
              'HIPAA-compliant by design',
              'CSV · JSON · FHIR export',
            ].map((stat, i, arr) => (
              <React.Fragment key={stat}>
                <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.78rem' }}>
                  {stat}
                </Typography>
                {i < arr.length - 1 && (
                  <Typography variant="caption" sx={{ color: '#334155', fontSize: '0.78rem' }}>·</Typography>
                )}
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
